import { ErrorBoundary } from '@genial/components';
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { cohortChartSelector } from '../../../../../../store/home/conversion/selector';

import { CohortCellTypes } from './components/CohortCell';
import { CohortFooter } from './components/CohortFooter';
import { CohortHeader } from './components/CohortHeader';
import { CohortRow } from './components/CohortRow';
import { cohortCellColumns, cohortCellGapVertical } from './constants';

export namespace CohortTypes {
  export type Props = {
    isMobile?: boolean;
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: cohortCellGapVertical,
    // @ts-expect-error
    minHeight: 'fit-content',
  },
});

export const Cohort: React.FC<CohortTypes.Props> = ({ isMobile }) => {
  const { data, loading, error, refresher } = useRecoilQuery(cohortChartSelector, {
    if: () => !isMobile,
  });

  if (isMobile) {
    return (
      <ErrorBoundary
        errorIconOrIllustration="ErrorChart"
        errorIconColor="brand.tertiary.principal"
        errorTitle="Visualização indisponível no celular"
        errorMessage="Este gráfico não está disponível para visualização no celular. Para acessá-lo, utilize um dispositivo com tela maior, como tablet ou computador."
      />
    );
  }

  if (error) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  const cohortRows =
    data?.months.map((month) => {
      const monthValues: CohortCellTypes.Props[] = month.values.map((item) => ({
        value: item.value,
        maxValue: 100,
        minValue: 0,
        monthNameFull: month.monthFullName,
        monthCycle: item.monthRef,
      }));

      return (
        <CohortRow
          key={month.monthName}
          cellsPerRow={cohortCellColumns}
          monthName={month.monthName}
          endPercentage={month.monthActivationCycle}
          monthValues={monthValues}
        />
      );
    }) ?? null;

  const averageValues: CohortCellTypes.Props[] | undefined = data?.average.map((item) => ({
    value: item.value,
    maxValue: 100,
    minValue: 0,
    withTooltip: false,
  }));

  return (
    <View style={styles.container}>
      <CohortHeader isLoading={loading} columns={cohortCellColumns} />
      {cohortRows}
      <CohortFooter
        isLoading={loading}
        annualPercentage={data?.annualAverage}
        monthValues={averageValues}
      />
    </View>
  );
};
